import * as T from '@bugbug/core/utils/toolbox';
import memoize from 'lodash.memoize';
import { createSelector } from 'reselect';

import type { RootState } from '../store';

import type {
  Organization,
  OrganizationSubscription,
  OrganizationSubscriptionSchedule,
  UserCurrentRole,
} from '@bugbug/core/types/organizations';
import { USER_ROLE } from '~/modules/constans';

import { formatOrganizationSubscription } from './organization.helpers';

export const selectOrganizationDomain = (state: RootState) => state.organization;

export const selectCurrentOrganizationId = createSelector(
  selectOrganizationDomain,
  (state) => state.currentOrganizationId,
);

export const selectOrganizations = createSelector(
  selectOrganizationDomain,
  (state) => state.organizations,
);

export const selectOrganizationsList = createSelector(selectOrganizations, (organizations) =>
  Object.values(organizations),
);

export const selectCurrentOrganization = createSelector(
  selectCurrentOrganizationId,
  selectOrganizations,
  (organizationId, organizations) => organizations[organizationId]!,
);

export const selectRoleInCurrentOrganization = createSelector(
  selectCurrentOrganization,
  (organization: Organization) => organization.currentUserRole,
);

export const selectIsOrganizationAdmin = createSelector(
  selectRoleInCurrentOrganization,
  (currentUserRole) =>
    ([USER_ROLE.ADMIN, USER_ROLE.IMPERSONATE] as UserCurrentRole[]).includes(currentUserRole),
);

export const selectIsOrganizationCreator = memoize((userId) =>
  createSelector(selectCurrentOrganization, (organization) => organization?.createdBy === userId),
);

export const selectInvoices = createSelector(
  selectOrganizationDomain,
  T.pathOr([], ['invoices', 'results']),
);

export const selectSubscription = createSelector(selectCurrentOrganization, (organization) => {
  if (!organization) return null;

  const formatted: OrganizationSubscription = formatOrganizationSubscription(
    organization.subscription,
  );

  return formatted;
});

export const selectSubscriptionSchedule = createSelector(
  selectCurrentOrganization,
  (organization) => {
    if (!organization?.subscriptionSchedule) return null;

    const formatted: OrganizationSubscriptionSchedule = formatOrganizationSubscription(
      organization.subscriptionSchedule,
    );

    return formatted;
  },
);

export const selectActivePaymentMethod = createSelector(selectSubscription, (subscription) => {
  if (!subscription?.defaultPaymentMethod) {
    return {};
  }
  return {
    id: subscription.defaultPaymentMethod.id,
    ...subscription.defaultPaymentMethod.card,
    brand: subscription.defaultPaymentMethod.card.brand.toUpperCase(),
  };
});

export const selectIsTrialEnded = createSelector(
  selectCurrentOrganization,
  (organization) => organization?.wasTrialUsed ?? false,
);

export const selectIsTrialAvailable = createSelector(
  selectSubscription,
  selectIsTrialEnded,
  (subscription, wasTrialUsed) => !wasTrialUsed && subscription?.type === 'free',
);

export const selectIsTrialActive = createSelector(selectSubscription, (s) => s?.isTrial ?? false);

export const selectIsFreePlan = createSelector(
  selectSubscription,
  (subscription) => subscription?.type === 'free',
);

export const selectParallelTestsLimit = createSelector(selectSubscription, (subscription) => {
  const DEFAULT_RUN_VALUE = 1;
  const addonValue =
    subscription?.addons.find((addon) => addon.type === 'extra_parallel_runs_limit')?.value ?? 0;
  return DEFAULT_RUN_VALUE + addonValue;
});

export const selectTestRunTimeLimit = createSelector(
  selectSubscription,
  (subscription) =>
    subscription?.addons.find((addon) => addon.type === 'test_run_time_limit')?.value ?? 0,
);
